import React from 'react';
import {useDropzone} from 'react-dropzone';
import {v4 as uuid} from 'uuid';
import {Stack} from '@mui/material';
import {
  Button,
  designSystemToken,
  Headline,
  Modal,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';

type ReplaceVideoModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  presentationId: string;
  setPresentationId: (presentationId: string) => void;
  createDropzoneConfig: (() => object) | null;
  headlineText: string;
  flowName: string;
  resetProject: () => void;
};

function ReplaceVideoModal({
  open,
  setOpen,
  presentationId,
  setPresentationId,
  createDropzoneConfig,
  headlineText,
  flowName,
  resetProject,
}: ReplaceVideoModalProps) {
  const {viewDismissed} = useViewDismissedEvent();
  const {uiInteraction} = useUiInteractionEvent();

  const onClose = (itemName: string, reason: string) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: itemName,
      interaction_location: CONTENT_LAB_VIEW_NAMES.REPLACE_VIDEO_MODAL,
      flow_name: flowName,
      interaction_id: interactionId,
    });
    viewDismissed({
      interaction_id: interactionId,
      presentation_id: presentationId,
      reason,
    });
    setPresentationId(uuid());
    setOpen(false);
  };

  const baseDropzoneConfig = createDropzoneConfig?.() ?? {};
  const replaceDropzoneConfig = {
    ...baseDropzoneConfig,
    noDrag: true,
    onDrop: async (files: File[]) => {
      resetProject();
      if ((baseDropzoneConfig as any).onDrop) {
        await (baseDropzoneConfig as any).onDrop(files);
      }
      onClose('replace', 'confirm');
    },
  } as any;

  const {getRootProps, getInputProps} = useDropzone(replaceDropzoneConfig);

  return (
    <Modal
      open={open}
      handleClose={() => onClose('cancel', 'cancel')}
      size="large"
    >
      <Stack direction="column">
        <Headline
          size="md"
          color={designSystemToken('semantic.fg.primary')}
          marginBottom="32px"
        >
          {headlineText}
        </Headline>
        <Stack direction="row" gap="8px" justifyContent="center">
          <Button
            appearance="neutral"
            mode="tinted"
            size="large"
            onClick={() => onClose('cancel', 'cancel')}
          >
            {translate('Cancel')}
          </Button>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Button
              appearance="neutral"
              mode="filled"
              size="large"
              onClick={() => {
                resetProject();
                onClose('replace', 'confirm');
              }}
            >
              {translate('Replace')}
            </Button>
          </div>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default ReplaceVideoModal;
